import React from 'react';
import {
    List, 
    Datagrid, 
    TextField, 
    EmailField,
    BooleanField,
    DateField,
    Edit,
    SimpleForm,
    BooleanInput,
    Create,
    required,
    email,
    Show,
    SimpleShowLayout,
    usePermissions,
    useRecordContext,
    useNotify,
    Button,
    CreateButton,
    TopToolbar,
    EditButton,
    ShowButton,
    DeleteButton,
    Pagination,
} from 'react-admin';
import { JumpstartTextInput, JumpstartCheckboxInput, JumpstartFormToolbar, JumpstartSearchInput } from '../components/JumpstartInputs';
import ContentCopy from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { API_URL } from '../config';
import { useT } from '../i18n/useT';

// Custom button to copy invitation code to clipboard
const CopyInvitationCodeButton = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const t = useT();
    
    if (!record) return null;
    
    const handleClick = () => {
        navigator.clipboard.writeText(record.code)
            .then(() => {
                notify(t('resources.invitations.notifications.codeCopied'), { type: 'success' });
            })
            .catch(() => {
                notify(t('resources.invitations.notifications.codeCopyFailed'), { type: 'error' });
            });
    };
    
    return (
        <Button
            label="actions.copyCode"
            onClick={handleClick}
            color="primary"
        >
            <ContentCopy />
        </Button>
    );
};

// Export invitations button
const ExportInvitationsButton = () => {
    const notify = useNotify();
    const t = useT();

    const handleExport = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                notify(t('notifications.authRequired'), { type: 'error' });
                return;
            }

            // Fetch all invitations
            const response = await fetch(`${API_URL}/auth/invitations?skip=0&limit=10000`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch invitations');
            }

            const invitations = await response.json();

            // Create CSV content
            const csvContent = [
                ['ID', 'Email', 'Code', 'Is Active', 'Is Admin', 'Invited By', 'Created At', 'Expires At'],
                ...invitations.map((inv: any) => [
                    inv.id,
                    inv.email,
                    inv.code,
                    inv.is_active ? 'Yes' : 'No',
                    inv.is_admin ? 'Yes' : 'No',
                    inv.invited_by || '',
                    inv.created_at,
                    inv.expires_at || '',
                ]),
            ]
                .map((row: any[]) => row.map((cell: any) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
                .join('\n');

            // Create and download CSV file
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `invitations_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            notify(t('resources.invitations.notifications.exportSuccess'), { type: 'success' });
        } catch (error) {
            console.error('Export error:', error);
            notify(t('resources.invitations.notifications.exportFailed'), { type: 'error' });
        }
    };

    return (
        <Button
            label="actions.exportCsv"
            onClick={handleExport}
            startIcon={<FileDownloadIcon />}
        />
    );
};

const InvitationListActions = () => {
    const { permissions } = usePermissions();
    return (
        <TopToolbar>
            <ExportInvitationsButton />
            {permissions === 'admin' && <CreateButton />}
        </TopToolbar>
    );
};

// List of invitations
export const InvitationList = () => {
    const { permissions } = usePermissions();
    const t = useT();
    
    // Only admin can see invitations
    if (permissions !== 'admin') {
        return <div>{t('resources.invitations.permissionDenied.view')}</div>;
    }
    
    return (
        <List 
            actions={<InvitationListActions />}
            perPage={25}
            pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
            filters={[
                <JumpstartSearchInput source="email" placeholder={t('resources.invitations.filters.emailPlaceholder')} alwaysOn key="email" />,
                <BooleanInput source="is_active" key="is_active" />
            ]}
            empty={<div style={{padding: '1rem', textAlign: 'center'}}>
                <h3>{t('resources.invitations.empty.title')}</h3>
                <p>{t('resources.invitations.empty.hint')}</p>
            </div>}
        >
            <Datagrid rowClick={false}>
                <TextField source="id" />
                <EmailField source="email" />
                <TextField source="code" />
                <BooleanField source="is_active" />
                <BooleanField source="is_admin" />
                <TextField source="invited_by" />
                <DateField source="created_at" />
                <DateField source="expires_at" />
                <ShowButton />
                <CopyInvitationCodeButton />
                <DeleteButton />
            </Datagrid>
        </List>
    );
};

// Create form for invitations (admin only)
export const InvitationCreate = () => {
    const { permissions } = usePermissions();
    const t = useT();
    
    // Only admin can create invitations
    if (permissions !== 'admin') {
        return <div>{t('resources.invitations.permissionDenied.create')}</div>;
    }
    
    return (
        <Create>
            <SimpleForm toolbar={<JumpstartFormToolbar saveLabel={t('resources.invitations.actions.createInvitation')} />}>
                <JumpstartTextInput source="email" validate={[required(), email()]} />
                <JumpstartCheckboxInput source="is_admin" defaultValue={false} />
            </SimpleForm>
        </Create>
    );
};

// Detailed view of an invitation
export const InvitationShow = () => {
    const { permissions } = usePermissions();
    const t = useT();
    
    // Only admin can see invitation details
    if (permissions !== 'admin') {
        return <div>{t('resources.invitations.permissionDenied.viewDetails')}</div>;
    }
    
    return (
        <Show 
            actions={
                <TopToolbar>
                    <DeleteButton />
                </TopToolbar>
            }
        >
            <SimpleShowLayout>
                <TextField source="id" />
                <EmailField source="email" />
                <TextField source="code" />
                <BooleanField source="is_active" />
                <BooleanField source="is_admin" />
                <TextField source="invited_by" />
                <DateField source="created_at" />
                <DateField source="expires_at" />
            </SimpleShowLayout>
        </Show>
    );
};

